import Button, { EButtonVariant, EButtonColor, EButtonSize } from "components/elements/Button";
import Typography from "components/elements/Typography";
import classes from "./classes.module.scss";
import classNames from "classnames";
import { container } from "tsyringe";
import StatusIcon from "components/materials/StatusIcon";
import Chip from "components/elements/Chip";
import { EyeIcon, ArrowDownTrayIcon } from "@heroicons/react/20/solid";
import { formatDate } from "utils/DateUtils";
import I18n from "components/materials/I18n";
import { useCallback, useContext, useEffect, useState } from "react";
import ProjectResponseResource from "common/resources/Project/ProjectResponseResource";
import Options from "./Options";
import AnimatedLoader from "components/elements/AnimatedLoader";
import ContributorResponseResource from "common/resources/Project/Config/Contributor/ContributorResponseResource";
import TeamService from "services/projects/TeamService";
import { AuthContext } from "contexts/Auth";
import { EProjectFilter } from "common/enums/Project/EProjectFilter";
import AnchorProjectModal from "components/materials/AnchorProjectModal";

const projectTeamService = container.resolve(TeamService);

type IProps = {
	project: ProjectResponseResource;
	onViewImageClick: () => void;
	onRowClick: () => void;
	downloadProof: (id: string, title: string) => void;
	onProjectChange: () => void;
};

export default function Row(props: IProps) {
	const { projectLeader } = useProjectLeader(props.project);
	const [isModalOpen, setIsModalOpen] = useState(false);

	const toogleModal = () => {
		setIsModalOpen(!isModalOpen);
	};

	const onAnchorProject = async () => {
		props.onProjectChange();
		toogleModal();
	};

	const date = () => {
		return (
			<Typography type="span" size="medium" weight="regular">
				{formatDate(I18n.getLocale(), new Date(props.project.createdAt ?? ""))}
			</Typography>
		);
	};

	const getProjectProStatus = useCallback(() => {
		if (props.project.archivedAt) {
			return EProjectFilter.ARCHIVED;
		}
		if (props.project.publishedAt) {
			return EProjectFilter.ONLINE;
		}
		return EProjectFilter.DRAFT;
	}, [props]);

	return (
		<tr className={classes["root"]}>
			<td className={classNames(classes["td-name"])} onClick={props.onRowClick}>
				<div className={classes["link"]}>
					<Typography className={classes["ellipsis"]}>{props.project.title}</Typography>
				</div>
				<div className={classes["responsive"]}>
					<StatusIcon status={getProjectProStatus()} />
					<Chip>{date()}</Chip>
				</div>
			</td>

			<td className={classes["td-author"]}>
				{projectLeader ? `${projectLeader?.firstName} ${projectLeader?.lastName}` : `${props.project.author?.firstName} ${props.project.author?.lastName}`}
			</td>

			<td className={classes["td-ai-image"]}>
				{props.project.poster?.file?.url ? (
					<Button onClick={props.onViewImageClick} variant={EButtonVariant.SELECTOR} color={EButtonColor.PRIMARY} size={EButtonSize.MEDIUM} className={classes["button"]}>
						<EyeIcon className={classes["icons"]} />
					</Button>
				) : (
					<AnimatedLoader />
				)}
			</td>

			<td className={classes["td-proof"]}>
				{props.project.scenario?.anchor?.txLink ? (
					<Button
						onClick={() => props.downloadProof(props.project.id, props.project.title)}
						variant={EButtonVariant.SELECTOR}
						color={EButtonColor.PRIMARY}
						size={EButtonSize.MEDIUM}
						className={classes["button"]}>
						<ArrowDownTrayIcon className={classes["icons"]} />
					</Button>
				) : props.project.scenario?.anchor?.id ? (
					<AnimatedLoader />
				) : (
					<Button onClick={toogleModal} variant={EButtonVariant.CONTAINED} color={EButtonColor.PRIMARY} size={EButtonSize.XSMALL}>
						{I18n.trslt(I18n.asset.pages.my_projects.table.anchor)}
					</Button>
				)}
			</td>

			<td className={classes["td-status"]}>
				<StatusIcon status={getProjectProStatus()} />
			</td>

			<td className={classes["td-date"]}>{date()}</td>

			<td className={classes["options"]}>
				<Options
					project={props.project}
					onRowClick={props.onRowClick}
					onProjectChange={props.onProjectChange}
					downloadProof={props.downloadProof}
					openAnchorModal={toogleModal}
				/>
			</td>
			<AnchorProjectModal project={props.project} isOpen={isModalOpen} onClose={toogleModal} onAnchorProject={onAnchorProject} isAdmin={false} />
		</tr>
	);
}

function useProjectLeader(project: Pick<ProjectResponseResource, "id">) {
	const { jwtContent } = useContext(AuthContext);
	const [projectLeader, setProjectLeader] = useState<ContributorResponseResource | null>(null);

	useEffect(() => {
		if (!jwtContent || !jwtContent.subscriptionPlan) return;
		projectTeamService
			.getByProjectId(project.id)
			.then((projectTeam) => {
				if (!projectTeam?.projectLeader) return;
				setProjectLeader(projectTeam.projectLeader);
			})
			.catch((error) => {
				console.error("Failed to fetch project leader", error);
			});
	}, [jwtContent, project]);

	return { projectLeader };
}
